import cn from 'classnames/bind'
import {
  useEffect,
  useState,
  RefCallback,
  useRef,
  ReactNode,
  ElementType,
  useCallback,
  cloneElement,
  Children,
  isValidElement,
} from 'react'

import styles from './page-template-content.module.scss'

const cx = cn.bind(styles)
const TRANSITION_OUT_TIME = 300 as const

type Props = {
  Component?: ElementType
  className?: string
  skipPadding?: boolean
  borderBottom?: boolean
  borderTop?: boolean
  smoothCorners?: boolean
  smoothCornersTop?: boolean
  smoothCornersBottom?: boolean
  shadowTop?: boolean
  shadowBottom?: boolean
  spaceBetweenVertical?: boolean
  skip?: boolean // open/close content
  animate?: boolean
  horizontalLayout?: boolean
  horizontalLayoutTop?: boolean
  hasBottomSpace?: boolean
  reference?: RefCallback<any>
  bottomFilterArrowDecoration?: boolean
  bottomFilterArrowDecorationShift?: number
  children: ReactNode
  flex?: boolean
  removeTopSmoothCorners?: boolean
  removeBottomSmoothCorners?: boolean
}

const PageTemplateContent = ({
  Component = 'div',
  className,
  skipPadding,
  borderBottom = true,
  borderTop,
  smoothCorners,
  smoothCornersTop,
  smoothCornersBottom,
  shadowTop,
  shadowBottom,
  spaceBetweenVertical,
  skip,
  animate,
  horizontalLayout,
  horizontalLayoutTop,
  hasBottomSpace,
  reference,
  bottomFilterArrowDecoration,
  bottomFilterArrowDecorationShift,
  children,
  flex,
  removeTopSmoothCorners,
  removeBottomSmoothCorners,
}: Props) => {
  const refEl = useRef<HTMLElement | null>(null)
  const refChild = useRef<HTMLElement | null>(null)
  const ro = useRef<ResizeObserver | null>(null)

  const [maxHeight, setMaxHeight] = useState(0)
  const [transition, setTransition] = useState(!skip)
  const [visibility, setVisibility] = useState(!skip)

  const handleMount = useCallback(
    (node: HTMLElement | null) => {
      if (reference) reference(node)
      refEl.current = node
    },
    [reference],
  )

  useEffect(() => {
    // открытие
    if (!skip) {
      setMaxHeight(refEl.current?.scrollHeight || 0)
      setTransition(true)
      setTimeout(() => {
        setTransition(false)
        if (ro.current && refChild.current) {
          ro.current.observe(refChild.current)
        }
      }, TRANSITION_OUT_TIME)
      setVisibility(!skip)
      return
    }
    // закрытие
    if (ro.current) ro.current.disconnect()
    setMaxHeight(0)
    setTransition(true)
    setTimeout(() => {
      setVisibility(!skip)
      setTransition(false)
    }, TRANSITION_OUT_TIME)
  }, [skip])

  const style = animate ? { maxHeight, opacity: visibility ? 1 : 0 } : {}

  const rootClassName = cx(
    styles.root,
    {
      padding: !skipPadding,
      borderTop,
      borderBottom,
      smoothCorners,
      smoothCornersTop,
      smoothCornersBottom,
      shadowTop,
      shadowBottom,
      spaceBetweenVertical,
      animate,
      horizontalLayout,
      horizontalLayoutTop,
      hasBottomSpace,
      flex,
      removeTopSmoothCorners,
      removeBottomSmoothCorners,
    },
    className,
  )

  const handleChildResize = useCallback(() => {
    if (!transition) {
      setMaxHeight(refEl.current?.scrollHeight || 0)
    }
  }, [transition])

  useEffect(() => {
    const root = document.documentElement
    if (bottomFilterArrowDecorationShift && root) {
      root.style.setProperty('--arrow-left-shift', `${bottomFilterArrowDecorationShift}px`)
    }
  }, [bottomFilterArrowDecorationShift])

  useEffect(() => {
    ro.current = new ResizeObserver(handleChildResize)
    return () => {
      if (ro.current) ro.current.disconnect()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const clonedChildren = Children.map(children, (child) => {
    if (isValidElement(child) && animate) {
      return cloneElement(child, { ref: refChild } as any)
    }
    return child
  })

  return (
    <Component className={rootClassName} ref={handleMount} style={style}>
      {clonedChildren}
      {bottomFilterArrowDecoration && (
        <div className={styles.arrowContainer}>
          <span className={styles.arrowUp} />
        </div>
      )}
    </Component>
  )
}

export default PageTemplateContent
